import "./App.css";
import React, { useEffect, useState } from "react";
import KinesisWebRTC from "./components/kinesis-webrtc";
import WelcomeScreen from "./components/WelcomeScreen";
import EndScreen from "./components/EndScreen";
import { decryptToken } from "./utils/decryptToken";
import { initializeConfig } from "./config";

// Screen states
const SCREENS = {
  WELCOME: "WELCOME",
  VERIFICATION: "VERIFICATION",
  EXAM: "EXAM",
  END: "END",
};

function App() {
  // eslint-disable-next-line no-unused-vars
  const [sessionInfo, setSessionInfo] = useState(null);
  const [currentScreen, setCurrentScreen] = useState(SCREENS.WELCOME);
  const [channelName, setChannelName] = useState("");
  const [hasCamera, setHasCamera] = useState(false);
  const [hasMicrophone, setHasMicrophone] = useState(false);
  const [isChecking, setIsChecking] = useState(false);
  const [deviceError, setDeviceError] = useState("");
  const [verifiedStudents, setVerifiedStudents] = useState({});
  const [examStartTime, setExamStartTime] = useState(null);
  const [examEndTime, setExamEndTime] = useState(null);

  useEffect(() => {
    const initializeApp = async () => {
      try {
        // Initialize AWS credentials
        await initializeConfig();

        const decodedInfo = decryptToken();
        console.log("Decoded session info:", decodedInfo);

        if (decodedInfo?.channelName) {
          const awsCompatibleChannelName = decodedInfo.channelName
            .replace(/[^a-zA-Z0-9_.-]/g, "_")
            .replace(/_{2,}/g, "_");

          console.log("Original channel name:", decodedInfo.channelName);
          console.log("AWS compatible channel name:", awsCompatibleChannelName);

          setChannelName(awsCompatibleChannelName);
          setSessionInfo({
            ...decodedInfo,
            originalChannelName: decodedInfo.channelName,
            channelName: awsCompatibleChannelName,
          });

          // Use token times
          if (decodedInfo.startTime && decodedInfo.endTime) {
            // Convert UTC times to local times
            const startTime = new Date(Number(decodedInfo.startTime));
            const endTime = new Date(Number(decodedInfo.endTime));
            const verificationStartTime = new Date(
              startTime.getTime() - 30 * 60 * 1000
            );

            setExamStartTime(startTime);
            setExamEndTime(endTime);

            // Log times in local format
            console.log("Exam Timing (Local):", {
              startTime: startTime.toLocaleString(),
              endTime: endTime.toLocaleString(),
              verificationStartsAt: verificationStartTime.toLocaleString(),
            });
          }
        }
        // Run system check on component mount
        checkDevices();
      } catch (error) {
        console.error("Error initializing app:", error);
      }
    };

    initializeApp();
  }, []);

  const checkDevices = async () => {
    setIsChecking(true);
    setDeviceError(""); // Clear previous errors
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: true,
        audio: true,
      });
      setHasCamera(true);
      setHasMicrophone(true);
      // Clean up the stream
      stream.getTracks().forEach((track) => track.stop());
    } catch (error) {
      console.error("Error accessing devices:", error);
      if (error.name === "NotAllowedError") {
        setHasCamera(false);
        setHasMicrophone(false);
        setDeviceError(
          "Permission denied! Please allow access to camera and microphone in your browser settings."
        );
      } else if (error.name === "NotFoundError") {
        setHasCamera(false);
        setHasMicrophone(false);
        setDeviceError(
          "No camera or microphone found! Please connect the required devices and try again."
        );
      } else if (error.name === "NotReadableError") {
        setHasCamera(false);
        setHasMicrophone(false);
        setDeviceError(
          "Could not access your devices. They might be in use by another application."
        );
      } else {
        setDeviceError(
          "An error occurred while accessing your devices. Please check your hardware connections."
        );
      }
    }
    setIsChecking(false);
  };

  const startProctoring = () => {
    const now = new Date();

    // Check if exam has already ended
    if (examEndTime && now >= examEndTime) {
      alert(`Exam has already ended at ${examEndTime.toLocaleString()}`);
      return;
    }

    // Check if exam has already started
    if (examStartTime && now >= examStartTime) {
      alert(`Exam has already started at ${examStartTime.toLocaleString()}`);
      return;
    }

    // Then check if we're within 30 minutes of exam start time
    const verificationStartTime = new Date(
      examStartTime.getTime() - 30 * 60 * 1000
    );
    if (now < verificationStartTime) {
      alert(
        `Verification can only start at ${verificationStartTime.toLocaleString()}`
      );
      return;
    }

    if (!hasCamera || !hasMicrophone) {
      alert(
        "Please ensure both camera and microphone are available before starting the session."
      );
      return;
    }
    setCurrentScreen(SCREENS.VERIFICATION);
    window.appState = {
      role: "MASTER",
      channelName: channelName,
      autoStart: true,
      examStartTime: examStartTime,
      examEndTime: examEndTime,
      examId: sessionInfo?.examId,
    };
    console.log("Starting verification with:", window.appState);
  };

  const handleVerificationComplete = () => {
    setCurrentScreen(SCREENS.EXAM);
  };

  const stopSession = () => {
    window.appState = null;
    setCurrentScreen(SCREENS.END);
    setVerifiedStudents({}); // Clear verified students state
    console.log("Session stopped");
  };

  return (
    <div
      className={`app-container ${
        currentScreen === SCREENS.EXAM ? "exam-mode" : ""
      }`}
    >
      {currentScreen === SCREENS.WELCOME && (
        <WelcomeScreen
          channelName={channelName}
          isChecking={isChecking}
          hasCamera={hasCamera}
          hasMicrophone={hasMicrophone}
          deviceError={deviceError}
          checkDevices={checkDevices}
          startProctoring={startProctoring}
        />
      )}
      {(currentScreen === SCREENS.VERIFICATION ||
        currentScreen === SCREENS.EXAM) && (
        <KinesisWebRTC
          onStreamStopped={stopSession}
          mode={
            currentScreen === SCREENS.VERIFICATION ? "verification" : "exam"
          }
          verifiedStudents={verifiedStudents}
          setVerifiedStudents={setVerifiedStudents}
          onVerificationComplete={handleVerificationComplete}
        />
      )}
      {currentScreen === SCREENS.END && (
        <EndScreen channelName={channelName} examId={sessionInfo?.examId} />
      )}
    </div>
  );
}

export default App;
